import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Download, FileText } from "lucide-react";
import CtaBanner from "@/components/CtaBanner";

const articles = [
  {
    id: 1,
    tag: "Guide",
    icon: BookOpen,
    title: "How to introduce Mental Health First Aid to your leadership team",
    excerpt: "A practical, non-corporate guide for HR leads making the case for MHFA to sceptical boards and budget-holders.",
    read: "6 min read",
    date: "Jan 2026",
  },
  {
    id: 2,
    tag: "Article",
    icon: FileText,
    title: "The MHFA Action Plan — a first aider's most important tool",
    excerpt: "ALGEE isn't just a mnemonic. Here's how each step actually plays out in real workplace conversations.",
    read: "8 min read",
    date: "Dec 2025",
  },
  {
    id: 3,
    tag: "Resource",
    icon: Download,
    title: "Free download: Wellbeing conversation starter cards",
    excerpt: "A printable set of 12 prompts to make it easier for line managers to open up meaningful check-ins.",
    read: "PDF · Free",
    date: "Nov 2025",
  },
  {
    id: 4,
    tag: "Article",
    icon: FileText,
    title: "What makes a good Mental Health First Aider? Six qualities to look for",
    excerpt: "Choosing the right people to become MHFAiders matters more than the number you train. Here's how to spot them.",
    read: "5 min read",
    date: "Oct 2025",
  },
  {
    id: 5,
    tag: "Guide",
    icon: BookOpen,
    title: "Supporting MHFAiders after certification — the piece most workplaces miss",
    excerpt: "Training is only the start. This is how to keep your first aiders confident, resourced and safe.",
    read: "7 min read",
    date: "Sep 2025",
  },
];

export default function Resources() {
  return (
    <div data-testid="resources-page" className="pt-36">
      <div className="container-x">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="eyebrow mb-4">Resources</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1E2A25] max-w-4xl">
            Articles, guides & <span className="italic text-[#4A5D52]">free tools</span> for better workplace wellbeing.
          </h1>
          <p className="mt-8 max-w-xl text-lg text-[#4A5D52] leading-relaxed">
            Practical writing from Liz and the Rise Learning team — no jargon, no fluff, just what
            we&apos;ve learned running MHFA courses across the UK.
          </p>
        </motion.div>
      </div>

      <div className="container-x mt-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {articles.map((a, i) => {
            const Icon = a.icon;
            const spans = [
              "md:col-span-7",
              "md:col-span-5",
              "md:col-span-5",
              "md:col-span-7",
              "md:col-span-12",
            ];
            return (
              <motion.article
                key={a.id}
                data-testid={`resource-${a.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`${spans[i]} group bg-[#F3EFEA] rounded-3xl p-8 md:p-10 hover:-translate-y-1 transition-transform duration-500 cursor-pointer`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#FDFBF7] text-[#4A5D52] flex items-center justify-center">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </span>
                    <span className="eyebrow text-[#D88C76]">{a.tag}</span>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#4A5D52] group-hover:rotate-45 transition-transform duration-500" />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#1E2A25] leading-snug">{a.title}</h3>
                <p className="mt-4 text-[#4A5D52] leading-relaxed">{a.excerpt}</p>
                <div className="mt-8 flex items-center gap-4 text-sm text-[#4A5D52]">
                  <span>{a.read}</span>
                  <span className="w-1 h-1 rounded-full bg-[#8DA399]" />
                  <span>{a.date}</span>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      <div className="mt-24">
        <CtaBanner />
      </div>
    </div>
  );
}
